import React, { useState, useEffect } from 'react';
import { Plus, Trash, AlertCircle, Info, Loader } from 'lucide-react';
import ApiService from '../../services/api';

const ClusterManagement = ({ environmentName, environmentId, onUpdate }) => {
    const [clusters, setClusters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAddingCluster, setIsAddingCluster] = useState(false);
    const [error, setError] = useState(null);
    const [newCluster, setNewCluster] = useState({
        name: '',
        serviceName: '',
        ssmParameterId: ''
    });

    useEffect(() => {
        fetchClusters();
    }, [environmentName]);

    const fetchClusters = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Fetch clusters for this environment
            const response = await ApiService.getEnvironment(environmentName);
            if (response.data && response.data.clusters) {
                setClusters(response.data.clusters);
            }
        } catch (error) {
            console.error('Error fetching clusters:', error);
            setError('Failed to load clusters. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCluster(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddCluster = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Validate input
            if (!newCluster.name || !newCluster.serviceName || !newCluster.ssmParameterId) {
                setError('All fields are required');
                setIsLoading(false);
                return;
            }

            // Create a cluster object with environment ID
            const clusterData = {
                ...newCluster,
                environmentId: environmentName,
                id: `${environmentName}-${newCluster.name}`
            };

            // Make API call to add cluster
            await ApiService.addCluster(clusterData);

            // Reset form and refresh clusters
            setNewCluster({
                name: '',
                serviceName: '',
                ssmParameterId: ''
            });

            setIsAddingCluster(false);
            fetchClusters();

            // Notify parent component
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Error adding cluster:', error);
            setError(`Failed to add cluster: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCluster = async (clusterId) => {
        try {
            setIsLoading(true);
            setError(null);

            // Make API call to delete cluster
            await ApiService.deleteCluster(clusterId);

            // Refresh clusters
            fetchClusters();

            // Notify parent component
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Error deleting cluster:', error);
            setError(`Failed to delete cluster: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading && clusters.length === 0) {
        return (
            <div className="flex justify-center items-center h-32">
                <Loader className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Clusters</h3>
                <button
                    onClick={() => setIsAddingCluster(true)}
                    className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Cluster
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <span className="text-sm text-red-700">{error}</span>
                    </div>
                </div>
            )}

            {clusters.length === 0 ? (
                <div className="bg-gray-50 p-4 rounded-md text-gray-500 text-sm">
                    No clusters found for this environment.
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cluster Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Service Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SSM Parameter ID
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {clusters.map((cluster) => (
                            <tr key={cluster.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {cluster.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cluster.serviceName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {cluster.ssmParameterId}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => handleDeleteCluster(cluster.id)}
                                        className="text-red-600 hover:text-red-900"
                                    >
                                        <Trash className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add Cluster Modal */}
            {isAddingCluster && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Add New Cluster</h3>
                            <button
                                onClick={() => setIsAddingCluster(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cluster Name*
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={newCluster.name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., prod-cluster"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Service Name*
                                </label>
                                <input
                                    type="text"
                                    name="serviceName"
                                    value={newCluster.serviceName}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., web-service"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    SSM Parameter ID*
                                </label>
                                <input
                                    type="text"
                                    name="ssmParameterId"
                                    value={newCluster.ssmParameterId}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., /moneysaver/clusters/desired-count"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    This parameter stores the desired task count in SSM Parameter Store
                                </p>
                            </div>

                            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                                <div className="flex">
                                    <Info className="h-5 w-5 text-blue-400 mr-2" />
                                    <p className="text-sm text-blue-700">
                                        The cluster will be associated with the {environmentName} environment.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setIsAddingCluster(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddCluster}
                                disabled={isLoading}
                                className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                                    isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                            >
                                {isLoading ? (
                                    <span className="flex items-center">
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Adding...
                  </span>
                                ) : (
                                    'Add Cluster'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClusterManagement;